import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  ArtifactApiError,
  ArtifactContent,
  ArtifactShare,
  OwnedArtifact,
  getArtifact,
  getArtifactInSharedConversation,
} from '../../shared/services/artifactsApi';
import ArtifactFrame from './ArtifactFrame';
import ArtifactShareDialog from './ArtifactShareDialog';
import { ARTIFACT_TYPE_LABELS, artifactDownload, slugifyFilename } from './artifactDocument';
import { ArtifactRef, closeArtifact, useArtifactViewer } from './artifactViewerStore';

const PANEL_WIDTH = 'clamp(420px, 46vw, 960px)';
const MOBILE_QUERY = '(max-width: 900px)';

const Panel = styled.aside<{ $full: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $full }) => ($full ? '100vw' : PANEL_WIDTH)};
  /* Acima da barra do chat (1150) e dos menus dela (1200); diálogos ficam em 9999. */
  z-index: ${({ $full }) => ($full ? 1300 : 1250)};
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.base_color};
  color: ${({ theme }) => theme.font_color};
  border-left: 1px solid ${({ theme }) => `${theme.text_secondary}33`};
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.08);
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 8px 8px 8px 16px;
  border-bottom: 1px solid ${({ theme }) => `${theme.text_secondary}26`};
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Sub = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  width: fit-content;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.text_secondary};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.blue_color};
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: ${({ theme }) => theme.user_message_background};
  border: 1px solid ${({ theme }) => `${theme.text_secondary}26`};
`;

const Seg = styled.button<{ $on: boolean }>`
  all: unset;
  cursor: pointer;
  font-size: 0.78rem;
  padding: 4px 12px;
  border-radius: 999px;
  color: ${({ theme, $on }) => ($on ? theme.font_color : theme.text_secondary)};
  background: ${({ theme, $on }) => ($on ? theme.base_color : 'transparent')};
  box-shadow: ${({ $on }) => ($on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none')};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.blue_color};
  }
`;

const Body = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  background: #fff;
`;

const Code = styled.pre`
  margin: 0;
  height: 100%;
  overflow: auto;
  padding: 16px;
  font: 12.5px/1.55 ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  white-space: pre;
  tab-size: 2;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1115' : '#f7f7f9')};
  color: ${({ theme }) => theme.font_color};
`;

const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  font-size: 0.9rem;
  color: #555;
  background: ${({ theme }) => theme.base_color};
`;

type Loaded = (ArtifactContent & Partial<OwnedArtifact>) | null;

function errorMessage(e: unknown): string {
  if (e instanceof ArtifactApiError) {
    if (e.status === 404) return 'Este artefato não está mais disponível.';
    if (e.status === 401) return 'Faça login para ver este artefato.';
    if (e.status === 403) return 'Você não tem acesso a este artefato.';
  }
  return 'Não foi possível carregar o artefato.';
}

async function load(ref: ArtifactRef, version: number | undefined, token: () => Promise<string | null>): Promise<Loaded> {
  if (ref.source.kind === 'conversation') {
    return getArtifactInSharedConversation(ref.source.shareId, ref.artifactId, version, await token());
  }
  const t = await token();
  if (!t) throw new ArtifactApiError(401, '');
  return getArtifact(t, ref.artifactId, version);
}

/** Painel lateral (tela cheia no celular) com o artefato aberto. Montado uma vez por página. */
const ArtifactPanel: React.FC = () => {
  const { ref, nonce } = useArtifactViewer();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [data, setData] = useState<Loaded>(null);
  const [version, setVersion] = useState<number | undefined>(undefined);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'preview' | 'code'>('preview');
  const [fullscreen, setFullscreen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [versionMenu, setVersionMenu] = useState<HTMLElement | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  const token = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      return await getAccessTokenSilently();
    } catch {
      return null;
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  // Nova abertura (cartão ou evento ao vivo): volta para a versão da mensagem.
  useEffect(() => {
    setVersion(ref?.version);
    setView('preview');
    setCopied(false);
  }, [ref?.artifactId, ref?.version, nonce]);

  useEffect(() => {
    if (!ref) {
      setData(null);
      setFullscreen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    load(ref, version, token)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setData(null);
          setError(errorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ref, version, nonce, token]);

  // Desktop: o chat encolhe para o painel caber ao lado (ver Content/ChatInputArea).
  useEffect(() => {
    const root = document.documentElement;
    if (ref && !isMobile && !fullscreen) root.style.setProperty('--artifact-panel-width', PANEL_WIDTH);
    else root.style.removeProperty('--artifact-panel-width');
    return () => { root.style.removeProperty('--artifact-panel-width'); };
  }, [ref, isMobile, fullscreen]);

  useEffect(() => {
    if (!ref) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || shareOpen) return;
      if (fullscreen && !isMobile) setFullscreen(false);
      else closeArtifact();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ref, fullscreen, isMobile, shareOpen]);

  if (!ref) return null;

  const title = data?.title || ref.title;
  const type = data?.type || ref.artifactType;
  const isOwner = ref.source.kind === 'owner';
  const versions = data?.versions || [];
  const shownVersion = data?.version ?? version;

  const copyCode = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* sem permissão de clipboard */ }
  };

  const download = () => {
    if (!data) return;
    const { ext, mime } = artifactDownload(type);
    const url = URL.createObjectURL(new Blob([data.content], { type: `${mime};charset=utf-8` }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugifyFilename(title)}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const onShareChange = (share: ArtifactShare | null) => {
    setData((d) => (d ? { ...d, share } : d));
  };

  const full = fullscreen || isMobile;
  const versionLabel = shownVersion
    ? `${ARTIFACT_TYPE_LABELS[type] || 'Artefato'} · versão ${shownVersion}${
        data?.latest_version && shownVersion !== data.latest_version ? ` de ${data.latest_version}` : ''
      }`
    : ARTIFACT_TYPE_LABELS[type] || 'Artefato';

  return (
    <Panel $full={full} aria-label={`Artefato: ${title}`}>
      <Header>
        <TitleBlock>
          <Title title={title}>{title}</Title>
          <Sub
            type="button"
            disabled={!isOwner || versions.length < 2}
            onClick={(e) => setVersionMenu(e.currentTarget)}
            aria-label="Escolher versão"
          >
            {versionLabel}
            {isOwner && versions.length > 1 && <ExpandMoreRoundedIcon style={{ fontSize: 16 }} />}
          </Sub>
        </TitleBlock>
        <Segmented role="tablist" aria-label="Modo de visualização">
          <Seg type="button" role="tab" aria-selected={view === 'preview'} $on={view === 'preview'} onClick={() => setView('preview')}>
            Prévia
          </Seg>
          <Seg type="button" role="tab" aria-selected={view === 'code'} $on={view === 'code'} onClick={() => setView('code')}>
            Código
          </Seg>
        </Segmented>
        {!isMobile && (
          <Tooltip title={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}>
            <IconButton size="small" onClick={() => setFullscreen((f) => !f)} aria-label="Tela cheia">
              {fullscreen ? <CloseFullscreenRoundedIcon fontSize="small" /> : <OpenInFullRoundedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Recarregar">
          <IconButton size="small" onClick={() => setReloadKey((k) => k + 1)} aria-label="Recarregar" disabled={!data}>
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={copied ? 'Copiado' : 'Copiar código'}>
          <IconButton size="small" onClick={copyCode} aria-label="Copiar código" disabled={!data}>
            {copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Baixar">
          <IconButton size="small" onClick={download} aria-label="Baixar" disabled={!data}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isOwner && (
          <Tooltip title={data?.share ? 'Link ativo · gerenciar' : 'Compartilhar'}>
            <IconButton
              size="small"
              onClick={() => setShareOpen(true)}
              aria-label="Compartilhar"
              disabled={!data}
              color={data?.share ? 'primary' : 'default'}
            >
              <IosShareRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Fechar">
          <IconButton size="small" onClick={closeArtifact} aria-label="Fechar">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Header>
      <Body>
        {data && view === 'preview' && (
          <ArtifactFrame key={`${data.artifact_id}-${data.version}-${reloadKey}`} type={type} content={data.content} title={title} />
        )}
        {data && view === 'code' && <Code>{data.content}</Code>}
        {loading && !data && <Center>Carregando…</Center>}
        {error && <Center>{error}</Center>}
      </Body>
      <Menu anchorEl={versionMenu} open={!!versionMenu} onClose={() => setVersionMenu(null)}>
        {[...versions].reverse().map((v) => (
          <MenuItem
            key={v.version}
            selected={v.version === shownVersion}
            onClick={() => {
              setVersion(v.version);
              setVersionMenu(null);
            }}
            sx={{ fontSize: '0.85rem' }}
          >
            Versão {v.version}
            {v.reason === 'edit' ? ' · edição' : ''}
            {v.created_at ? ` · ${new Date(v.created_at.endsWith('Z') ? v.created_at : `${v.created_at}Z`).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
          </MenuItem>
        ))}
      </Menu>
      {isOwner && data && (
        <ArtifactShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          artifactId={data.artifact_id}
          title={title}
          share={data.share || null}
          onShareChange={onShareChange}
        />
      )}
    </Panel>
  );
};

export default ArtifactPanel;
